/** Display currencies for donate page (checkout remains USD). */
export const DONATE_DISPLAY_CURRENCIES = [
  { code: "USD", label: "US Dollar ($)" },
  { code: "EUR", label: "Euro (€)" },
  { code: "GBP", label: "British Pound (£)" },
  { code: "CAD", label: "Canadian Dollar (CA$)" },
  { code: "AUD", label: "Australian Dollar (A$)" },
  { code: "NZD", label: "New Zealand Dollar (NZ$)" },
  { code: "JPY", label: "Japanese Yen (¥)" },
  { code: "KRW", label: "South Korean Won (₩)" },
  { code: "CNY", label: "Chinese Yuan (CN¥)" },
  { code: "TWD", label: "Taiwan Dollar (NT$)" },
  { code: "HKD", label: "Hong Kong Dollar (HK$)" },
  { code: "SGD", label: "Singapore Dollar (S$)" },
  { code: "MYR", label: "Malaysian Ringgit (RM)" },
  { code: "THB", label: "Thai Baht (฿)" },
  { code: "IDR", label: "Indonesian Rupiah (Rp)" },
  { code: "PHP", label: "Philippine Peso (₱)" },
  { code: "VND", label: "Vietnamese Dong (₫)" },
  { code: "INR", label: "Indian Rupee (₹)" },
  { code: "PKR", label: "Pakistani Rupee (₨)" },
  { code: "AED", label: "UAE Dirham (د.إ)" },
  { code: "SAR", label: "Saudi Riyal (﷼)" },
  { code: "ILS", label: "Israeli Shekel (₪)" },
  { code: "CHF", label: "Swiss Franc (CHF)" },
  { code: "SEK", label: "Swedish Krona (SEK)" },
  { code: "NOK", label: "Norwegian Krone (NOK)" },
  { code: "DKK", label: "Danish Krone (DKK)" },
  { code: "PLN", label: "Polish Złoty (PLN)" },
  { code: "CZK", label: "Czech Koruna (Kč)" },
  { code: "HUF", label: "Hungarian Forint (Ft)" },
  { code: "RON", label: "Romanian Leu (lei)" },
  { code: "TRY", label: "Turkish Lira (₺)" },
  { code: "ZAR", label: "South African Rand (R)" },
  { code: "EGP", label: "Egyptian Pound (E£)" },
  { code: "BRL", label: "Brazilian Real (R$)" },
  { code: "MXN", label: "Mexican Peso (MX$)" },
  { code: "ARS", label: "Argentine Peso (AR$)" },
  { code: "CLP", label: "Chilean Peso (CL$)" },
  { code: "COP", label: "Colombian Peso (COL$)" },
  { code: "PEN", label: "Peruvian Sol (S/)" },
];

/** Fallback approx rates vs USD if the live feed is unavailable or omits a code. */
export const FALLBACK_USD_RATES = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.53,
  NZD: 1.66,
  JPY: 151,
  KRW: 1350,
  CNY: 7.2,
  TWD: 32,
  HKD: 7.8,
  SGD: 1.34,
  MYR: 4.45,
  THB: 35,
  IDR: 15800,
  PHP: 56,
  VND: 25400,
  INR: 83,
  PKR: 278,
  AED: 3.67,
  SAR: 3.75,
  ILS: 3.65,
  CHF: 0.88,
  SEK: 10.5,
  NOK: 10.7,
  DKK: 6.9,
  PLN: 3.95,
  CZK: 23,
  HUF: 360,
  RON: 4.55,
  TRY: 32,
  ZAR: 18.5,
  EGP: 49,
  BRL: 5.0,
  MXN: 17.2,
  ARS: 950,
  CLP: 940,
  COP: 4100,
  PEN: 3.75,
};

const RATES_CACHE_KEY = "nocturne_donate_fx_v2";
const RATES_TTL_MS = 6 * 60 * 60 * 1000;

const ZERO_DECIMAL_CURRENCIES = new Set([
  "JPY",
  "KRW",
  "IDR",
  "VND",
  "CLP",
  "COP",
  "HUF",
]);

export function formatDonateMoney(centsUsd, currency = "USD", rate = 1) {
  const code = String(currency || "USD").toUpperCase();
  const amount = ((Number(centsUsd) || 0) / 100) * (Number(rate) || 1);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2,
      minimumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code)
        ? 0
        : amount < 10
          ? 2
          : 0,
    }).format(amount);
  } catch {
    return `$${((Number(centsUsd) || 0) / 100).toFixed(2)}`;
  }
}

export async function fetchUsdRates() {
  try {
    const raw = localStorage.getItem(RATES_CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (
        cached?.rates &&
        cached.fetchedAt &&
        Date.now() - cached.fetchedAt < RATES_TTL_MS
      ) {
        return { rates: cached.rates, source: "cache" };
      }
    }
  } catch {
    // ignore cache parse errors
  }

  try {
    const res = await fetch("https://api.frankfurter.app/latest?from=USD");
    if (!res.ok) throw new Error(`fx ${res.status}`);
    const data = await res.json();
    // Live ECB rates override fallback; fallback fills codes Frankfurter omits.
    const rates = { ...FALLBACK_USD_RATES, USD: 1, ...(data.rates || {}) };
    try {
      localStorage.setItem(
        RATES_CACHE_KEY,
        JSON.stringify({ rates, fetchedAt: Date.now() }),
      );
    } catch {
      // ignore quota
    }
    return { rates, source: "live" };
  } catch {
    return { rates: { ...FALLBACK_USD_RATES }, source: "fallback" };
  }
}

export function detectPreferredCurrency() {
  try {
    const saved = localStorage.getItem("nocturne_donate_currency");
    if (
      saved &&
      DONATE_DISPLAY_CURRENCIES.some((c) => c.code === saved.toUpperCase())
    ) {
      return saved.toUpperCase();
    }
  } catch {
    // ignore
  }
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale || "en-US";
    const guess = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
    }).resolvedOptions().currency;
    const region = locale.split("-")[1]?.toUpperCase();
    const regionMap = {
      US: "USD",
      GB: "GBP",
      CA: "CAD",
      AU: "AUD",
      NZ: "NZD",
      JP: "JPY",
      KR: "KRW",
      CN: "CNY",
      TW: "TWD",
      HK: "HKD",
      SG: "SGD",
      MY: "MYR",
      TH: "THB",
      ID: "IDR",
      PH: "PHP",
      VN: "VND",
      IN: "INR",
      PK: "PKR",
      AE: "AED",
      SA: "SAR",
      IL: "ILS",
      CH: "CHF",
      SE: "SEK",
      NO: "NOK",
      DK: "DKK",
      PL: "PLN",
      CZ: "CZK",
      HU: "HUF",
      RO: "RON",
      TR: "TRY",
      ZA: "ZAR",
      EG: "EGP",
      BR: "BRL",
      MX: "MXN",
      AR: "ARS",
      CL: "CLP",
      CO: "COP",
      PE: "PEN",
      DE: "EUR",
      FR: "EUR",
      ES: "EUR",
      IT: "EUR",
      NL: "EUR",
      IE: "EUR",
      PT: "EUR",
      AT: "EUR",
      BE: "EUR",
      FI: "EUR",
    };
    const mapped = region && regionMap[region];
    if (
      mapped &&
      DONATE_DISPLAY_CURRENCIES.some((c) => c.code === mapped)
    ) {
      return mapped;
    }
    if (
      guess &&
      DONATE_DISPLAY_CURRENCIES.some((c) => c.code === guess)
    ) {
      return guess;
    }
  } catch {
    // ignore
  }
  return "USD";
}
